import { inject } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
  HttpBackend,
  HttpClient,
} from '@angular/common/http';
import {
  BehaviorSubject,
  catchError,
  Observable,
  switchMap,
  throwError,
  filter,
  take,
  mergeMap,
  of
} from 'rxjs';
import { IAuthAccessToken, ELocalStorageKeys } from '@account/data-access';
import { AuthStore } from '@auth/data-access';
import { APP_CONFIG, AppConfig, AppSource } from '@application/config';
import { LocalStorageFacade } from '@application/localstorage';

let isRefreshing = false;
const refreshTokenSubject: BehaviorSubject<IAuthAccessToken | null> = new BehaviorSubject<IAuthAccessToken | null>(null);
let authData: IAuthAccessToken | null = null;
const resolveAuthStore = () => inject(AuthStore);
type AuthStoreInstance = ReturnType<typeof resolveAuthStore>;
let authStoreInstance: AuthStoreInstance | null = null;
export const TokenInterceptor: HttpInterceptorFn = (
  request: HttpRequest<any>,
  next: HttpHandlerFn,
): Observable<HttpEvent<any>> => {
  const appConfig = inject<AppConfig>(APP_CONFIG);
  const httpBackend = inject(HttpBackend);
  const httpNoInterceptors = new HttpClient(httpBackend);
  const storage = inject(LocalStorageFacade);

  const getAuthStore = (): AuthStoreInstance | null => {
    if (authStoreInstance) {
      return authStoreInstance;
    }

    try {
      authStoreInstance = resolveAuthStore();
    } catch {
      authStoreInstance = null;
    }

    return authStoreInstance;
  };

  const readAuth = (): IAuthAccessToken | null => {
    const store = getAuthStore();
    const storeAuth = store?.getAuthData();
    if (storeAuth) {
      return storeAuth;
    }

    try {
      const prefixed = storage.getItem(ELocalStorageKeys.auth);
      const rawPrefixed = prefixed ?? localStorage.getItem(`actuar/${ELocalStorageKeys.auth}`);
      const raw = rawPrefixed ?? localStorage.getItem(ELocalStorageKeys.auth);
      return raw ? (JSON.parse(raw) as IAuthAccessToken) : null;
    } catch {
      return null;
    }
  };

  const writeAuth = (data: IAuthAccessToken) => {
    getAuthStore()?.setAuthData(data);
    try {
      storage.setItem(ELocalStorageKeys.auth, JSON.stringify(data));
    } catch {
      // noop
    }
  };

  const addAuthToken = (req: HttpRequest<any>): HttpRequest<any> => {
    if (!req.withCredentials) {
      return req;
    }

    authData = readAuth();
    if (!authData?.Token) {
      return req.clone({ withCredentials: false });
    }

    let headers = req.headers.set('Authorization', `Bearer ${authData.Token}`);

    if (!(req.body instanceof FormData)) {
      headers = headers.set('Content-Type', 'application/json');
    }

    return req.clone({
      withCredentials: false,
      headers
    });
  };

  const waitForRefreshToken = refreshTokenSubject.pipe(
    filter((authData) => authData !== null),
    take(1),
    switchMap(() => next(addAuthToken(request))),
    catchError((error) => {
      if (isRefreshing) {
        refreshTokenSubject.next(null);
        isRefreshing = false;
      }
      return throwError(() => error);
    })
  );

  const handle401Error = (
    req: HttpRequest<any>,
    next: HttpHandlerFn,
  ): Observable<HttpEvent<any>> => {
    if (!isRefreshing) {
      isRefreshing = true;
      refreshTokenSubject.next(null);

      const current = readAuth();
      if (!current?.TokenId) {
        isRefreshing = false;
        return throwError(() => new HttpErrorResponse({
          status: 401,
          statusText: 'Unauthorized - User not logged in'
        }));
      }

      const body = {
        TokenId: current.TokenId,
        AppSource: AppSource[appConfig.appSource],
      } as any;

      return httpNoInterceptors
        .post<IAuthAccessToken>(
          appConfig.apiUrl.userService + '/Auth/Refresh',
          body
        )
        .pipe(
          switchMap((newAuth) => {
            isRefreshing = false;
            writeAuth(newAuth);
            authData = newAuth;
            refreshTokenSubject.next(newAuth);
            return next(addAuthToken(req));
          }),
          catchError((error) => {
            isRefreshing = false;
            refreshTokenSubject.next(null);

            if (error.status === 400) {
              getAuthStore()?.logout();
            }

            return throwError(() => error);
          })
        );
    }

    return waitForRefreshToken
  };

  const authenticatedRequest = addAuthToken(request);

  return of(isRefreshing).pipe(
    switchMap((isRefreshing) => {
      if (!isRefreshing) {
        return next(authenticatedRequest);
      }
      return waitForRefreshToken;
    }),
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 && request.withCredentials) {
        return handle401Error(request, next);
      }
      return throwError(() => error);
    })
  );
};
